import {
  QueryClient,
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
  UseInfiniteQueryResult,
} from '@tanstack/react-query';

import { QUERY_KEY_ROOTS } from 'variables';
import { QueryOptionsConfig, usePaginatedApi } from 'api/hooks';
import {
  CompletedEvaluation,
  EvaluatorLink,
  PaginatedResponse,
} from 'api/httpEntities';
import { PractitionerData } from 'components/EventForms/types';
import {
  type DefaultPageParam,
  type QueryParamsBase,
  type SearchMode,
  STARTS_WITH,
} from 'api/search';
import {
  deleteEvaluatorLink,
  getCompletedEvaluators,
  getEvaluatorLink,
  getNotifiablePractitioners,
  updateNotificationSettings,
  updateReferralCodeMandatory,
} from 'api';
import { EvaluatorSearchFilter } from './evaluatorLinksApiTypes';

export interface GetCompletedEvaluatorsRequest extends QueryParamsBase {
  id: string;
  searchTerm?: string;
  purchaseStatus?: EvaluatorSearchFilter;
  searchMode?: SearchMode;
}

export const useDeleteEvaluatorLink = (): UseMutationResult<
  void,
  unknown,
  string,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteEvaluatorLink,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.ORGANISATION_EVALUATOR_LINKS],
      });
    },
  });
};

export const useGetEvaluatorLink = (
  id: string,
  config: QueryOptionsConfig<EvaluatorLink> = {},
): UseQueryResult<EvaluatorLink, Error> =>
  useQuery<EvaluatorLink, Error>({
    queryKey: [QUERY_KEY_ROOTS.ORGANISATION_EVALUATOR_LINKS, id],
    queryFn: async () => getEvaluatorLink(id),
    ...config,
  });

export const useGetCompletedEvaluators = (
  id: string,
  searchTerm = '',
  searchMode: SearchMode = STARTS_WITH,
  purchaseStatus: EvaluatorSearchFilter = 'ALL',
): UseInfiniteQueryResult<
  {
    items: CompletedEvaluation[];
    totalCount?: number;
  },
  Error
> =>
  usePaginatedApi<
    PaginatedResponse<CompletedEvaluation>,
    CompletedEvaluation,
    DefaultPageParam & GetCompletedEvaluatorsRequest
  >({
    queryKey: [
      QUERY_KEY_ROOTS.ORGANISATION_EVALUATOR_LINK_EVALUATIONS,
      id,
      searchTerm,
      purchaseStatus,
      searchMode,
    ],
    fetchPage: getCompletedEvaluators,
    getItemsFromPage: (p) => p.response ?? [],
    queryParams: { id, searchTerm, purchaseStatus, searchMode },
  });

export const useUpdateNotificationSettings = (
  queryClient: QueryClient,
): UseMutationResult<
  void,
  Error,
  { evaluatorId: string; practitionerIds: string[] },
  unknown
> =>
  useMutation({
    mutationFn: updateNotificationSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.ORGANISATION_EVALUATOR_LINKS],
      });
    },
  });

export const useUpdateReferralCodeMandatory = (
  queryClient: QueryClient,
): UseMutationResult<
  void,
  Error,
  { evaluatorId: string; isReferralCodeMandatory: boolean },
  unknown
> =>
  useMutation({
    mutationFn: updateReferralCodeMandatory,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.ORGANISATION_EVALUATOR_LINKS],
      });
    },
  });

export const useGetNotifiablePractitioners = (
  evaluatorId: string,
  config: QueryOptionsConfig<PractitionerData> = {},
): UseQueryResult<PractitionerData, Error> =>
  useQuery<PractitionerData, Error>({
    queryKey: [
      QUERY_KEY_ROOTS.ORGANISATION_EVALUATOR_LINK_EVALUATIONS,
      evaluatorId,
    ],
    queryFn: async () => getNotifiablePractitioners(evaluatorId),
    ...config,
  });
